import type { NestedFilter, CommonSort } from '@@partner:types:graphql'

import React, { useState, useCallback, useMemo } from 'react'
import { useFetchOrders } from '@@partner:graphql/query/order/allOrders.gql'
import { useParams } from 'react-router-dom'

import { Message, Pagination, ButtonGroup, Button } from 'rsuite'
import OrdersTable from '@components/order/table/OrdersTable'
import OrdersFilter from '@components/order/table/OrdersFilter'
import KanbanBoard from '@components/order/kanban/KanbanBoard'
import { groupOrdersByStatusGroups } from '@components/order/kanban/kanbanHelpers'
import type { KanbanStatusGroup } from '@components/order/kanban/kanbanHelpers'

import { ORDERS_PER_PAGE_LIMIT } from '@@partner:config/index'
import { convertToInt } from '@@partner:helpers/util'
import OrdersPageTopMenu from '@components/order/OrdersPageTopMenu'
import { ORDER } from '@hive/status'

// Все статусы для запроса заказов на Kanban-доску
const ORDERS_KANBAN_STATUSES = [
  ORDER.CONFIRMED,
  ORDER.ASSEMBLY_IN_PROGRESS,
  ORDER.ASSEMBLY_COMPLETE,
  ORDER.WAITING_FOR_PAYMENT,
  ORDER.WAITING_FOR_COURIER,
  ORDER.WAITING_FOR_PICKUP,
  ORDER.ACCEPTED_BY_COURIER,
  ORDER.DELIVERY_IN_PROGRESS
]

// Группы статусов для колонок Kanban
const ORDERS_KANBAN_GROUPS: KanbanStatusGroup[] = [
  {
    key: 'confirmed',
    label: 'Размещён',
    statuses: [ ORDER.CONFIRMED ]
  },
  {
    key: 'assembling',
    label: 'Собирается',
    statuses: [ ORDER.ASSEMBLY_IN_PROGRESS, ORDER.ASSEMBLY_COMPLETE, ORDER.WAITING_FOR_PAYMENT ]
  },
  {
    key: 'waiting_courier',
    label: 'Ожидает курьера',
    statuses: [ ORDER.WAITING_FOR_COURIER, ORDER.WAITING_FOR_PICKUP ]
  },
  {
    key: 'delivery',
    label: 'В доставке',
    statuses: [ ORDER.ACCEPTED_BY_COURIER, ORDER.DELIVERY_IN_PROGRESS ]
  }
]

type ViewMode = 'table' | 'board'

export default function OrdersPage () {
  const { brandSlug } = useParams<any>()
  const [ viewMode, setViewMode ] = useState<ViewMode>('table')
  const [ page, setPage ] = useState(1)
  const [ filter, setFilter ] = useState<NestedFilter>({
    fields: [ 'id' ],
    op: 'unwrap'
  })
  const [ sort, setSort ] = useState<CommonSort>({
    fieldName: 'deliverySlot.ts_start',
    direction: 0
  })

  // Для режима доски - фильтруем по статусам и получаем больше заказов
  const kanbanFilter = useMemo<NestedFilter>(() => {
    const baseFilter: NestedFilter = {
      fields: [ 'status' ],
      op: 'contains',
      value: ORDERS_KANBAN_STATUSES
    }

    if (filter.op === 'unwrap') {
      return { and: [ baseFilter ] }
    }

    return { and: [ baseFilter, filter ] }
  }, [ filter ])

  const { data, loading, error } = useFetchOrders({
    pager: {
      page: viewMode === 'board' ? 1 : page,
      limit: viewMode === 'board' ? 10 : ORDERS_PER_PAGE_LIMIT
    },
    filter: viewMode === 'board' ? kanbanFilter : {
      and: [ 
        filter,
        
        {
          fields: [ 'status' ],
          op: 'not_eq',
          value: 0
        } 
      ]
    },
    sort,
    allActive: viewMode === 'board' ? true : false,
    pollInterval: 30000
  })

  const handlePageSelect = useCallback((selectedPage: number | string) => {
    setPage(convertToInt(selectedPage))
  }, [])

  const handleFilterChanged = useCallback((updatedFilter: NestedFilter) => {
    setFilter(updatedFilter)
    setPage(1)
  }, [])

  const handleSortColumn = useCallback((sortColumn, sortType) => {
    setSort({
      fieldName: sortColumn,
      direction: sortType === 'desc' ? 0 : 1
    })
  }, [])

  const orders = useMemo(() => data?.orders?.edges ?? [], [ data?.orders?.edges ])
  const pageInfo = data?.orders?.pageInfo

  const showPagination = viewMode === 'table' && pageInfo?.lastPage !== 1

  // Группировка заказов по группам статусов для Kanban
  const ordersByGroup = useMemo(() => {
    return groupOrdersByStatusGroups(orders, ORDERS_KANBAN_GROUPS)
  }, [ orders ])

  return (
    <div className="OrdersPage">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 className="u-heading" style={{ margin: 0 }}>Заказы</h1>
        <ButtonGroup size="xs">
          <Button
            appearance={viewMode === 'table' ? 'primary' : 'default'}
            onClick={() => setViewMode('table')}
          >
            Таблица
          </Button>
          <Button
            appearance={viewMode === 'board' ? 'primary' : 'default'}
            onClick={() => setViewMode('board')}
          >
            Доска
          </Button>
        </ButtonGroup>
      </div>

      <OrdersPageTopMenu />

      {error && (
        <Message
          showIcon
          type="error"
          header="При получении данных возникла ошибка!"
        >
          {error.message}
        </Message>
      )}

      <OrdersFilter onFilterChanged={handleFilterChanged} />

      {viewMode === 'table' ? (
        <>
          {orders && (
            <OrdersTable
              brandSlug={brandSlug}
              orders={orders}
              loading={loading}
              onSortColumn={handleSortColumn}
              sortColumn={sort.fieldName}
              sortType={sort.direction === 0 ? 'desc' : 'asc'}
            />
          )}

          {showPagination && (
            <Pagination
              size="md"
              last
              first
              next
              prev
              ellipsis
              activePage={page}
              pages={pageInfo?.lastPage}
              total={pageInfo?.lastPage ?? 1}
              maxButtons={10}
              onSelect={handlePageSelect}
            />
          )}
        </>
      ) : (
        <KanbanBoard
          brandSlug={brandSlug}
          ordersByGroup={ordersByGroup}
          loading={loading}
          groups={ORDERS_KANBAN_GROUPS}
        />
      )}
    </div>
  )
}
